import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '@/shared/ui/SearchInput';
import {
  buildLevelFilterOptions,
  buildStatusFilterOptions,
  type BriefTemplateFilterOption,
  type BriefTemplateLevelFilter,
  type BriefTemplateStatusFilter,
} from '../../constants/briefTemplates.constants';

interface BriefTemplatesToolbarFilters {
  level: BriefTemplateLevelFilter;
  status: BriefTemplateStatusFilter;
  onLevelChange: (value: BriefTemplateLevelFilter) => void;
  onStatusChange: (value: BriefTemplateStatusFilter) => void;
}

interface BriefTemplatesToolbarProps {
  /** Controlled search input value. Parent owns debounce + filtering. */
  search: string;
  /** Fires synchronously on each keystroke with the new input value. */
  onSearchChange: (value: string) => void;
  /** Fires when the user clicks the "New template" CTA button. */
  onCreateClick: () => void;
  /**
   * Whether the viewer may create a template. False drops the CTA rather than
   * disabling it: a role with view-only has no path to enabling it, so a dead
   * button is only an invitation to a 403.
   */
  canCreate?: boolean;
  /** Level and Status selects beside the search. Omitted, the toolbar has search alone. */
  filters?: BriefTemplatesToolbarFilters;
}

function FilterSelect<T extends string>(props: {
  label: string;
  value: T;
  options: BriefTemplateFilterOption<T>[];
  onChange: (value: T) => void;
}) {
  return (
    <Select value={props.value} onValueChange={(value: string) => props.onChange(value as T)}>
      <SelectTrigger aria-label={props.label} className="w-44">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {props.options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Toolbar for the Brief Templates list view.
 *
 * Pure controlled component — no internal state, no debounce. The parent
 * (`BriefTemplatesList.tsx`) owns the search string, debounces it via
 * `useDebounce(SEARCH_DEBOUNCE_MS)`, and fires navigation when the CTA is
 * clicked.
 *
 * Mirrors `BriefsToolbar` (the gold standard) for layout + monks-ui +
 * lucide-react palette consistency.
 */
export function BriefTemplatesToolbar(props: BriefTemplatesToolbarProps) {
  const { t } = useTranslation('knowledge');

  return (
    <div className="flex items-center justify-between gap-4 px-section py-4">
      <div className="flex items-center gap-sm">
        <SearchInput
          placeholder={t('knowledgeHub.briefTemplatesToolbar.searchTemplatesByName')}
          aria-label={t('knowledgeHub.briefTemplatesToolbar.searchTemplatesLabel')}
          value={props.search}
          onChange={props.onSearchChange}
          className="w-80"
        />
        {props.filters && (
          <>
            <FilterSelect
              label={t('knowledgeHub.briefTemplatesToolbar.level')}
              value={props.filters.level}
              options={buildLevelFilterOptions()}
              onChange={props.filters.onLevelChange}
            />
            <FilterSelect
              label={t('knowledgeHub.briefTemplatesToolbar.status')}
              value={props.filters.status}
              options={buildStatusFilterOptions()}
              onChange={props.filters.onStatusChange}
            />
          </>
        )}
      </div>
      {props.canCreate !== false && (
        <Button size="default" onClick={props.onCreateClick}>
          <Plus className="size-4" />
          {t('knowledgeHub.briefTemplatesToolbar.newTemplate')}
        </Button>
      )}
    </div>
  );
}
